import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store';
import { CopilotProfile } from '@/types/copilot-profile';
import { copilotService, CreateCopilotProfileRequest } from '@/api/services/copilotService';

interface CopilotProfilesState {
  profiles: CopilotProfile[];
  selectedProfileId: string | null;
  loading: boolean;
  error: string | null;
  activeProfile: CopilotProfile | null;
  activeProfileLoading: boolean;
}

// Initial state with mock profiles including performance data
const initialState: CopilotProfilesState = {
  profiles: [
    {
      id: 'default-profile',
      name: 'Pullse Copilot',
      avatarUrl: '', // Will use the fallback icon
      description: 'Your intelligent support assistant',
      allowedActionIds: ['refund', 'ticket-update', 'customer-info'],
      isActive: true,
      createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days ago
      updatedAt: new Date().toISOString(),
      conversationsHandled: 1247,
      successRate: 94.5,
      lastActiveAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
      connectedContentCount: 23
    },
    {
      id: 'support-profile',
      name: 'Support Assistant',
      avatarUrl: '', // Will use the fallback icon
      description: 'Specialized in customer support issues',
      allowedActionIds: ['refund', 'ticket-update'],
      isActive: false,
      createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(), // 15 days ago
      updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
      conversationsHandled: 89,
      successRate: 87.2,
      lastActiveAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
      connectedContentCount: 8
    }
  ],
  selectedProfileId: 'default-profile',
  loading: false,
  error: null,
  activeProfile: null,
  activeProfileLoading: false,
};

// Async thunks
export const fetchCopilotProfiles = createAsyncThunk(
  'copilotProfiles/fetchProfiles',
  async () => {
    const response = await copilotService.getCopilotList();
    return response.data;
  }
);

export const createCopilotProfile = createAsyncThunk(
  'copilotProfiles/createCopilotProfile',
  async (data: CreateCopilotProfileRequest) => {
    const response = await copilotService.createCopilot(data);
    return response;
  }
);

export const fetchCopilotProfileById = createAsyncThunk(
  'copilotProfiles/fetchCopilotProfileById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await copilotService.getCopilot(id);
      return response;
    } catch (error: any) {
      const status = error?.response?.status || error?.originalError?.response?.status;
      const defaultMessage = error?.message || 'Failed to load copilot profile';
      return rejectWithValue({
        status,
        message: status === 404 ? 'The requested copilot profile could not be found.' : defaultMessage,
      });
    }
  }
);

export const updateCopilotProfile = createAsyncThunk(
  'copilotProfiles/updateCopilotProfile',
  async ({ id, data }: { id: string; data: CreateCopilotProfileRequest }) => {
    const response = await copilotService.updateCopilot(id, data);
    return response;
  }
);

// Slice
const copilotProfilesSlice = createSlice({
  name: 'copilotProfiles',
  initialState,
  reducers: {
    selectProfile(state, action: PayloadAction<string>) {
      state.selectedProfileId = action.payload;
    },
    clearSelectedProfile(state) {
      state.selectedProfileId = null;
    },
    updateProfile(state, action: PayloadAction<CopilotProfile>) {
      const index = state.profiles.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.profiles[index] = action.payload;
      }
    },
    setActiveProfile(state, action: PayloadAction<CopilotProfile | null>) {
      state.activeProfile = action.payload;
    },
    clearActiveProfile(state) {
      state.activeProfile = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCopilotProfiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCopilotProfiles.fulfilled, (state, action) => {
        state.loading = false;
        state.profiles = action.payload;
      })
      .addCase(fetchCopilotProfiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch copilot profiles';
      })
      .addCase(createCopilotProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCopilotProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profiles.push(action.payload);
      })
      .addCase(createCopilotProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create copilot profile';
      })
      .addCase(fetchCopilotProfileById.pending, (state) => {
        state.activeProfileLoading = true;
        state.error = null;
      })
      .addCase(fetchCopilotProfileById.fulfilled, (state, action) => {
        state.activeProfileLoading = false;
        state.activeProfile = action.payload;
      })
      .addCase(fetchCopilotProfileById.rejected, (state, action) => {
        state.activeProfileLoading = false;
        const payload = action.payload as { status?: number; message?: string } | undefined;
        state.error = payload?.message || action.error.message || 'Failed to load copilot profile';
        if (!payload?.status || payload.status !== 404) {
          state.activeProfile = null;
        }
      })
      .addCase(updateCopilotProfile.pending, (state) => {
        state.activeProfileLoading = true;
        state.error = null;
      })
      .addCase(updateCopilotProfile.fulfilled, (state, action) => {
        state.activeProfileLoading = false;
        state.activeProfile = action.payload;
        const index = state.profiles.findIndex(profile => profile.id === action.payload.id);
        if (index !== -1) {
          state.profiles[index] = action.payload;
        }
      })
      .addCase(updateCopilotProfile.rejected, (state, action) => {
        state.activeProfileLoading = false;
        state.error = action.error.message || 'Failed to update copilot profile';
      });
  },
});

// Actions
export const {
  selectProfile,
  clearSelectedProfile,
  updateProfile,
  setActiveProfile,
  clearActiveProfile,
} = copilotProfilesSlice.actions;

// Selectors
export const selectAllProfiles = (state: RootState) => state.copilotProfiles.profiles;
export const selectProfilesLoading = (state: RootState) => state.copilotProfiles.loading;
export const selectProfilesError = (state: RootState) => state.copilotProfiles.error;
export const selectSelectedProfileId = (state: RootState) => state.copilotProfiles.selectedProfileId;
export const selectSelectedProfile = (state: RootState) => {
  const { profiles, selectedProfileId } = state.copilotProfiles;
  return selectedProfileId ? profiles.find(p => p.id === selectedProfileId) : profiles.find(p => p.isActive);
};

export const selectActiveCopilotProfile = (state: RootState) => state.copilotProfiles.activeProfile;
export const selectActiveCopilotLoading = (state: RootState) => state.copilotProfiles.activeProfileLoading;

// Add this selector to fix the CopilotNode.tsx error
export const selectCopilotProfiles = (state: RootState) => 
  state.copilotProfiles && state.copilotProfiles.profiles ? state.copilotProfiles.profiles : [];

// Reducer
export const copilotProfilesReducer = copilotProfilesSlice.reducer;
